import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ErrorCallout } from "@/components/ui/error-callout";
import type { IntakeData } from "@/lib/types";
import { Loader2, Mail, RefreshCw, CheckCircle, Clock } from "lucide-react";
import { patientIntakeApi } from "@/lib/patientIntakeApi";
import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";

interface VerifyEmailStepProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onVerificationSuccess: () => void;
  onBack: () => void;
  formUid?: string;
  referenceUid?: string;
  /** When true (e.g. intake v2), skip the large hero header — the parent shows the step title. */
  embeddedInFlow?: boolean;
}

type AuthMode = "magic-link" | "otp";

const OTP_EXPIRY_SECONDS = 15 * 60;

const primaryCtaClass =
  "w-full h-12 text-sm sm:text-base font-semibold rounded-lg bg-[#166B93] text-white shadow-lg shadow-[#166B93]/20 transition-[background-color,box-shadow] hover:bg-[#145d82] active:bg-[#125473] disabled:opacity-50 disabled:shadow-none";

const outlineBtnClass =
  "w-full h-11 text-sm rounded-lg border border-gray-200 bg-white font-medium text-gray-800 shadow-sm transition-colors hover:bg-gray-50 active:bg-gray-100";

const secondaryOutlineClass =
  "w-full h-10 text-xs sm:text-sm rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50";

export default function VerifyEmailStep({
  data,
  onUpdate,
  onVerificationSuccess,
  onBack,
  formUid,
  referenceUid,
  embeddedInFlow = false,
}: VerifyEmailStepProps) {
  const { i18n } = useLingui();
  const [authMode, setAuthMode] = useState<AuthMode>("magic-link");
  const [otp, setOtp] = useState("");
  const [challengeId, setChallengeId] = useState<string>("");
  const [timeLeft, setTimeLeft] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [lastMagicLinkSentAt, setLastMagicLinkSentAt] = useState<string | null>(null);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const initializedRef = useRef(false);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const formattedMagicLinkTime = lastMagicLinkSentAt
    ? new Date(lastMagicLinkSentAt).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  useEffect(() => {
    if (!initializedRef.current && formUid && referenceUid) {
      initializedRef.current = true;
      void sendMagicLink();
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [formUid, referenceUid]);

  useEffect(() => {
    if (timeLeft <= 0) {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      return;
    }

    countdownRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return `${minutes}:${remainder.toString().padStart(2, "0")}`;
  };

  const sendMagicLink = async (options?: { forceResend?: boolean }) => {
    if (!formUid || !referenceUid) {
      setError(i18n._(t`Unable to send verification link. Missing intake reference.`));
      setErrorStatus(400);
      setAuthMode("otp");
      return;
    }

    setIsSending(true);
    setError("");
    setErrorStatus(null);
    setStatusMessage("");

    try {
      const result = await patientIntakeApi.startAuth({
        email: data.email,
        formUid,
        referenceUid,
        forceResend: options?.forceResend ?? false,
      });

      setLastMagicLinkSentAt(result.data?.sentAt ?? new Date().toISOString());

      if (result.data?.alreadySent) {
        setStatusMessage(
          i18n._(
            t`We've already sent a verification link to your email. Please check your inbox (including spam).`,
          ),
        );
      } else if (options?.forceResend) {
        setStatusMessage(
          i18n._(t`A fresh verification link is on its way. Check your email and click the link to finish.`),
        );
      } else if (result.data?.requiresOtpFallback) {
        setStatusMessage(
          i18n._(t`We emailed you a magic link. If you prefer, you can verify with a 6-digit code instead.`),
        );
      } else {
        setStatusMessage(
          i18n._(t`We just sent a secure verification link to your email. Click it to finish your intake.`),
        );
      }

      setAuthMode("magic-link");
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : i18n._(t`We couldn't send the verification link. We'll try a code instead.`);
      const errorStatus =
        err instanceof Error ? Number((err as { status?: number }).status) : 0;

      setError(errorMessage);
      setErrorStatus(errorStatus || null);
      if (errorStatus === 403) {
        setAuthMode("magic-link");
        return;
      }

      setAuthMode("otp");
      // Automatically fallback to OTP if the link fails
      setTimeout(() => {
        void sendOtp();
      }, 800);
    } finally {
      setIsSending(false);
    }
  };

  const sendOtp = async () => {
    if (!formUid) {
      setError(i18n._(t`Unable to send verification code. Missing intake reference.`));
      setErrorStatus(400);
      return;
    }

    setIsSending(true);
    setAuthMode("otp");
    setError("");
    setErrorStatus(null);
    setStatusMessage("");
    setOtp("");
    setRemainingAttempts(null);

    try {
      const result = await patientIntakeApi.sendVerificationCode(data.email, formUid);
      setChallengeId(result.data.challengeId);
      setTimeLeft(OTP_EXPIRY_SECONDS);
      setStatusMessage(
        i18n._(t`Verification code sent! Check your email (and spam folder) for a 6-digit code.`),
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : i18n._(t`We couldn't send the code right now. Please try again shortly.`);
      const status =
        err instanceof Error ? Number((err as { status?: number }).status) : 0;

      const minutesMatch = errorMessage.match(/(\d+)\s+minutes?/);
      const secondsMatch = errorMessage.match(/(\d+)\s+seconds?/);

      if (minutesMatch) {
        setTimeLeft(parseInt(minutesMatch[1], 10) * 60);
      } else if (secondsMatch) {
        setTimeLeft(parseInt(secondsMatch[1], 10));
      } else if (errorMessage.includes("few seconds")) {
        setTimeLeft(30);
      } else {
        setTimeLeft(0);
      }

      setError(errorMessage);
      setErrorStatus(status || null);
    } finally {
      setIsSending(false);
    }
  };

  const verifyOtp = async () => {
    if (!formUid) {
      setError(i18n._(t`We couldn't verify your code because the intake reference is missing.`));
      setErrorStatus(400);
      return;
    }

    if (otp.length !== 6) {
      setError(i18n._(t`Please enter the 6-digit code that we emailed you.`));
      setErrorStatus(null);
      return;
    }

    setIsVerifying(true);
    setError("");
    setErrorStatus(null);
    setStatusMessage("");

    try {
      const result = await patientIntakeApi.verifyEmail(
        data.email,
        otp,
        formUid,
        challengeId,
      );

      if (result.data?.accessToken) {
        localStorage.setItem("accessToken", result.data.accessToken);
        if (result.data.refreshToken) {
          localStorage.setItem("refreshToken", result.data.refreshToken);
        }
      }

      onUpdate({ emailVerified: true });
      setStatusMessage(i18n._(t`Email verified successfully! Redirecting to the final step...`));

      setTimeout(() => {
        onVerificationSuccess();
      }, 1200);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : i18n._(t`The verification code didn't work. Please try again.`);
      const status =
        err instanceof Error ? Number((err as { status?: number }).status) : 0;

      const attemptsMatch = errorMessage.match(/You have (\d+) attempt/);
      if (attemptsMatch) {
        setRemainingAttempts(parseInt(attemptsMatch[1], 10));
      } else {
        setRemainingAttempts(null);
      }

      if (errorMessage.toLowerCase().includes("expired")) {
        setTimeLeft(0);
      }

      setError(errorMessage);
      setErrorStatus(status || null);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleOtpChange = (value: string) => {
    const numeric = value.replace(/\D/g, "").slice(0, 6);
    setOtp(numeric);
    if (error) {
      setError("");
      setErrorStatus(null);
    }
    setRemainingAttempts(null);
  };

  const handleSwitchToOtp = () => {
    setAuthMode("otp");
    setError("");
    setErrorStatus(null);
    setStatusMessage("");
    if (!challengeId) {
      void sendOtp();
    }
  };

  return (
    <div
      className={`mx-auto flex w-full flex-col py-2 sm:py-4 ${embeddedInFlow ? "max-w-lg" : "max-w-xl"}`}
    >
      {!embeddedInFlow && (
        <div className="mb-6 text-center sm:mb-8">
          <div className="mb-4 flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#166B93]/15 bg-[#166B93]/10">
              <Mail className="h-7 w-7 text-[#166B93]" aria-hidden />
            </div>
          </div>
          <h1 className="text-[clamp(22px,5vw,36px)] font-bold leading-tight text-gray-900">
            <Trans>Thank you!</Trans>
          </h1>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-gray-500">
            <Trans>Your information has been successfully received.</Trans>
          </p>
        </div>
      )}

      <div className="space-y-5 rounded-2xl border border-gray-200/90 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.06)] sm:p-6">
        <div className="text-center">
          {!embeddedInFlow && (
            <p className="mb-3 text-sm font-medium text-gray-800 sm:text-base">
              <Trans>Just one last step: verify your email address.</Trans>
            </p>
          )}
          {embeddedInFlow && (
            <div className="mb-5 space-y-2">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-[1.75rem]">
                <Trans>Thank you!</Trans>
              </h1>
              <p className="text-sm leading-relaxed text-gray-600 sm:text-base">
                <Trans>Your information has been successfully received.</Trans>
              </p>
            </div>
          )}
          <span className="inline-flex items-center gap-2 rounded-full border border-[#166B93]/20 bg-[#166B93]/10 px-4 py-2 text-sm font-semibold text-[#166B93] sm:px-5">
            <Mail className="h-4 w-4 opacity-80" aria-hidden />
            {data.email}
          </span>
        </div>

        <div className="rounded-xl border border-gray-100 bg-[#F7F7F5] p-4 sm:p-5">
            {authMode === "magic-link" ? (
              <div className="space-y-5">
                {statusMessage && (
                  <div className="bg-white border border-[#166B93]/20 rounded-xl p-3.5 text-[#0F4461] text-sm leading-relaxed text-center shadow-sm">
                    <p className="font-medium">{statusMessage}</p>
                    {formattedMagicLinkTime && (
                      <p className="mt-1.5 text-xs text-[#0F4461]/60 flex items-center justify-center gap-1">
                        <Clock className="w-3 h-3" />
                        <Trans>Sent at {formattedMagicLinkTime}</Trans>
                      </p>
                    )}
                  </div>
                )}

                {error && (
                  <ErrorCallout message={error} status={errorStatus} />
                )}

                <div className="space-y-4 pt-1">
                  <Button
                    onClick={() => sendMagicLink({ forceResend: true })}
                    disabled={isSending}
                    className={primaryCtaClass}
                  >
                    {isSending ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <Trans>Sending...</Trans>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <RefreshCw className="w-5 h-5" />
                        <Trans>Resend Magic Link</Trans>
                      </div>
                    )}
                  </Button>

                  <div className="relative flex items-center py-2">
                    <div className="flex-grow border-t border-gray-200"></div>
                    <span className="flex-shrink-0 mx-4 text-xs text-gray-400 font-medium uppercase tracking-wider">
                      <Trans>Or</Trans>
                    </span>
                    <div className="flex-grow border-t border-gray-200"></div>
                  </div>

                  <div className="text-center">
                    <Button
                      variant="outline"
                      className={outlineBtnClass}
                      onClick={handleSwitchToOtp}
                    >
                      <Trans>Use a 6-Digit Code Instead</Trans>
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="grid gap-3">
                  <Label htmlFor="otp" className="text-sm font-semibold text-gray-700 text-center">
                    <Trans>Enter Verification Code</Trans>
                  </Label>
                  <Input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={(event) => handleOtpChange(event.target.value)}
                    placeholder="------"
                    className="h-14 rounded-lg border border-gray-300 bg-white text-center text-2xl font-bold tracking-[1rem] shadow-[0_2px_8px_rgba(0,0,0,0.02)] transition-all focus-visible:border-[#166B93] focus-visible:outline-none focus-visible:ring-0"
                    maxLength={6}
                    autoComplete="one-time-code"
                    disabled={isVerifying}
                  />
                  {timeLeft > 0 && (
                    <div className="flex items-center justify-center gap-1.5 text-xs font-medium text-gray-500 mt-0.5">
                      <Clock className="w-3.5 h-3.5" />
                      <Trans>
                        Code expires in {formatTime(timeLeft)}
                      </Trans>
                    </div>
                  )}
                </div>

                {statusMessage && !error && (
                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3.5 text-sm text-emerald-800 flex items-start gap-2.5 text-center shadow-sm">
                    <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                    <span className="font-medium">{statusMessage}</span>
                  </div>
                )}

                {error && (
                  <ErrorCallout message={error} status={errorStatus}>
                    {remainingAttempts !== null ? (
                      <p className="text-xs opacity-90 mt-1 font-medium">
                        {remainingAttempts === 0
                          ? i18n._(t`No attempts remaining. Please request a new code.`)
                          : i18n._(
                              t`{count, plural, one {You have # attempt left.} other {You have # attempts left.}}`,
                              { count: remainingAttempts },
                            )}
                      </p>
                    ) : null}
                  </ErrorCallout>
                )}

                <div className="space-y-3 pt-2">
                  <Button
                    onClick={verifyOtp}
                    disabled={isVerifying || otp.length !== 6}
                    className={primaryCtaClass}
                  >
                    {isVerifying ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <Trans>Verifying...</Trans>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        <Trans>Verify Email</Trans>
                      </div>
                    )}
                  </Button>
                  
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isSending}
                      className={secondaryOutlineClass}
                      onClick={() => void sendOtp()}
                    >
                      {isSending ? (
                        <div className="flex items-center gap-1.5">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          <Trans>Resending...</Trans>
                        </div>
                      ) : (
                        <Trans>Resend Code</Trans>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full h-10 rounded-lg border border-[#166B93]/30 bg-white text-xs font-medium text-[#166B93] hover:bg-[#166B93]/5 sm:text-sm"
                      onClick={() => {
                        setAuthMode("magic-link");
                        void sendMagicLink();
                      }}
                    >
                      <Trans>Use Magic Link</Trans>
                    </Button>
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>

      <div
        className={`mt-6 flex justify-center ${embeddedInFlow ? "hidden md:flex" : ""}`}
      >
        <button
          type="button"
          onClick={onBack}
          className="box-border flex h-12 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 text-sm font-medium text-gray-800 shadow-sm transition-colors hover:bg-gray-50 md:border-0 md:bg-transparent md:shadow-none md:hover:bg-gray-100/90"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          <Trans>Back to Review</Trans>
        </button>
      </div>
    </div>
  );
}

